import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import MobileBottomNav from '../components/MobileBottomNav';
import { sortByJobNumber } from '../utils/jobOrder';
import { useAuth } from '../context/AuthContext';
import JobSearchSelect from './JobSearchSelect';
import { setPreferredView, desktopHome } from '../services/viewMode';
import ChangePinModal from '../components/ChangePinModal';
import DirectiveCards from '../components/DirectiveCards';
import AskCards from '../components/AskCards';
import WinterizeList from '../components/WinterizeList';   // ❄ machines that still need winterizing — Done clears it for everyone (Ryan, Nov 2 2026)
import FieldUpdateBanner from '../components/FieldUpdateBanner';
import NotificationBell from '../components/NotificationBell';
import LogTimeOffSheet from '../components/LogTimeOffSheet';
import { MyInfoSheet } from '../components/MyInfo';
import { ReportIssueModal } from '../components/ReportIssue';   // 🐞 the same form as the desktop sidebar (Pat, Oct 5 2026)   // license, OSHA, CPR… (Pat, Oct 5 2026)
import TimeOffRequestSheet from './TimeOffModal';
import MyScheduleModal, { TodaySchedule } from './MyScheduleModal';   // read-only week + who's with you (Ryan, Oct 11 2026)   // the shared request (kinds, history) — PMs don't clock in, so the phone home is where they ask
import AttachButton, { AttachThumb, isPdf } from '../components/AttachButton';

import { jobNumber, jobName } from '../utils/ui';
import FourQuestions, { EMPTY_ANSWERS, composeUpdate, isSupervisorNow } from '../components/FourQuestions';
import { MyReminders } from '../components/Reminders';

// A work order is a small-division job. Same table, different paperwork — which is
// why the Projects list can show them together or one kind at a time.
const isWorkOrder = p => p.division === 'small';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';

function getToken() {
  try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; }
  catch { return null; }
}

function apiFetch(path, opts = {}) {
  const token = getToken();
  return fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(token ? { 'x-worker-token': token } : {}), ...opts.headers },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error || `Request failed (${r.status})`); return d; });
}


function DailyNoteModal({ projects, defaultProjectId, onClose }) {
  const [projectId, setProjectId] = useState(defaultProjectId || (projects.length === 1 ? projects[0].id : ''));
  const [note, setNote]           = useState('');
  // A field supervisor's Daily Update is four questions, sent as one labelled update (Ryan, Oct 14 2026).
  const fourQ = isSupervisorNow();
  const [answers, setAnswers]     = useState(EMPTY_ANSWERS);
  const [activeQ, setActiveQ]     = useState('did');
  const activeQRef = useRef('did');
  const text = fourQ ? composeUpdate(answers) : note;
  const [mediaFiles, setMediaFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [listening, setListening]  = useState(false);
  const [result, setResult]        = useState(null);
  const [error, setError]          = useState(null);
  const recognitionRef = useRef(null);
  const fileInputRef   = useRef(null);

  function startVoice() {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert('Voice input not supported on this browser.'); return; }
    const r = new SR();
    r.lang = 'en-US';
    r.continuous = true;
    r.interimResults = false;
    r.onresult = e => {
      const transcript = Array.from(e.results).map(r => r[0].transcript).join(' ');
      if (fourQ) { const k = activeQRef.current; setAnswers(a => ({ ...a, [k]: a[k] ? a[k] + ' ' + transcript : transcript })); }
      else setNote(n => n ? n + ' ' + transcript : transcript);
    };
    r.onend = () => setListening(false);
    r.start();
    recognitionRef.current = r;
    setListening(true);
  }

  function stopVoice() {
    recognitionRef.current?.stop();
    setListening(false);
  }

  function handleMediaChange(e) {
    const files = Array.from(e.target.files || []);
    setMediaFiles(prev => [...prev, ...files]);
  }

  function removeMedia(idx) {
    setMediaFiles(prev => prev.filter((_, i) => i !== idx));
  }

  async function submit() {
    if (!projectId || !text.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      let mediaUrls = [];
      if (mediaFiles.length > 0) {
        setUploading(true);
        const token = (() => { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; } })();
        const fd = new FormData();
        mediaFiles.forEach(f => fd.append('files', f));
        const upRes = await fetch(`${API_BASE}/upload`, {
          method: 'POST',
          headers: token ? { 'x-worker-token': token } : {},
          body: fd,
        });
        const upData = await upRes.json().catch(() => ({}));
        if (!upRes.ok) throw new Error(upData.error || 'Photo upload failed');
        mediaUrls = upData.urls || [];
        setUploading(false);
      }
      const res = await apiFetch(`/projects/${projectId}/notes`, {
        method: 'POST',
        body: JSON.stringify({ note: text, mediaUrls }),
      });
      setResult(res);
    } catch (e) {
      setError('Could not submit. Try again.');
      setUploading(false);
    } finally {
      setSubmitting(false);
    }
  }

  return createPortal(
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
      <div style={{ background: '#fff', borderRadius: '18px 18px 0 0', padding: '24px 20px 44px', boxShadow: '0 -4px 24px rgba(0,0,0,0.18)', maxHeight: '90vh', overflowY: 'auto' }}>
        {result ? (
          <>
            <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 8, color: '#111' }}>Note submitted ✓</div>
            {result.updatedSubJobs?.length > 0 && (
              <p style={{ color: '#666', fontSize: 14, lineHeight: 1.55, margin: '0 0 20px' }}>
                AI updated summaries for: <strong style={{ color: '#111' }}>{result.updatedSubJobs.join(', ')}</strong>
              </p>
            )}
            <button
              onClick={onClose}
              style={{ width: '100%', padding: '14px 0', background: '#e87722', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}
            >Done</button>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#111' }}>Daily Update</div>
              <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 26, cursor: 'pointer', color: '#999', lineHeight: 1, padding: 0 }}>×</button>
            </div>

            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Job {defaultProjectId && projectId === defaultProjectId ? '· clocked in' : ''}</label>
            <div style={{ marginBottom: 18 }}>
              <JobSearchSelect value={projectId} projects={projects} onChange={setProjectId} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{fourQ ? "Today's update — four quick questions" : 'What did you work on today?'}</label>
              <button
                onMouseDown={e => { e.preventDefault(); listening ? stopVoice() : startVoice(); }}
                style={{ background: listening ? '#e87722' : '#f0f0f0', border: 'none', borderRadius: 20, padding: '5px 12px', fontSize: 12, fontWeight: 700, color: listening ? '#fff' : '#666', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor"><rect x="4" y="0" width="4" height="7" rx="2"/><path d="M2 6a4 4 0 008 0" stroke="currentColor" strokeWidth="1.2" fill="none" strokeLinecap="round"/><line x1="6" y1="10" x2="6" y2="12" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
                {listening ? 'Stop' : 'Speak'}
              </button>
            </div>
            {fourQ ? (
              <div style={{ marginBottom: 12 }}><FourQuestions value={answers} onChange={setAnswers} active={listening ? activeQ : null} onFocusField={k => { setActiveQ(k); activeQRef.current = k; }} /></div>
            ) : (
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="e.g. Finished the horizontal bore on the south end. Started pulling wire on loops. Waiting on NVE to schedule inspection."
                rows={4}
                style={{ width: '100%', marginBottom: 12, padding: '12px 14px', borderRadius: 10, border: `1.5px solid ${listening ? '#e87722' : '#ddd'}`, fontSize: 15, resize: 'none', background: '#fff', color: '#111', fontFamily: 'inherit', boxSizing: 'border-box', lineHeight: 1.5, transition: 'border-color 0.2s' }}
              />
            )}

            {/* Media attachments */}
            <div style={{ marginBottom: 16 }}>
              <input ref={fileInputRef} type="file" accept="image/*,video/*" multiple style={{ display: 'none' }} onChange={handleMediaChange} />
              <button
                onClick={() => fileInputRef.current?.click()}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f5f5f5', border: '1.5px dashed #ccc', borderRadius: 10, padding: '10px 16px', fontSize: 13, color: '#666', cursor: 'pointer', width: '100%', justifyContent: 'center' }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="1" y="3" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.3"/><circle cx="5.5" cy="7.5" r="1.5" fill="currentColor"/><path d="M1 11l4-4 3 3 2-2 5 5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Add photos or video
              </button>
              {mediaFiles.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                  {mediaFiles.map((f, i) => (
                    <div key={i} style={{ position: 'relative' }}>
                      {f.type.startsWith('image/') ? (
                        <img src={URL.createObjectURL(f)} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8 }} />
                      ) : (
                        <div style={{ width: 72, height: 72, background: '#222', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11 }}>VIDEO</div>
                      )}
                      <button onClick={() => removeMedia(i)} style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: '#d00', color: '#fff', border: 'none', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {error && <div style={{ color: '#d00', fontSize: 13, marginBottom: 12 }}>{error}</div>}

            <button
              onClick={submit}
              disabled={!projectId || !text.trim() || submitting || uploading}
              style={{ width: '100%', padding: '14px 0', background: (!projectId || !text.trim() || submitting) ? '#ccc' : '#e87722', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}
            >{uploading ? 'Uploading media…' : submitting ? 'Submitting…' : 'Submit Update'}</button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

function TimeOffModal({ onClose }) {
  const [form, setForm] = useState({ start_date: '', end_date: '', reason: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const [myRequests, setMyRequests] = useState([]);
  const [tab, setTab] = useState('new'); // 'new' | 'history'

  useEffect(() => {
    apiFetch('/my-time-off').then(setMyRequests).catch(() => {});
  }, []);

  async function submit() {
    if (!form.start_date || !form.end_date) return setErr('Please select start and end dates');
    if (form.end_date < form.start_date) return setErr('End date must be on or after start date');
    setSubmitting(true); setErr('');
    try {
      await apiFetch('/time-off', { method: 'POST', body: JSON.stringify(form) });
      setDone(true);
      apiFetch('/my-time-off').then(setMyRequests).catch(() => {});
    } catch (e) { setErr(e.message); }
    finally { setSubmitting(false); }
  }

  const statusStyle = s => ({
    pending:  { background: '#fef3c7', color: '#92400e' },
    approved: { background: '#d1fae5', color: '#065f46' },
    denied:   { background: '#fee2e2', color: '#991b1b' },
  }[s] || {});

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 480, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#111' }}>Time Off Request</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, color: '#999', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ display: 'flex', borderBottom: '1px solid #f0f0f0' }}>
          {['new', 'history'].map(t => (
            <button key={t} onClick={() => { setTab(t); setDone(false); }}
              style={{ flex: 1, padding: '10px 0', fontSize: 13, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', color: tab === t ? '#e87722' : '#9ca3af', borderBottom: tab === t ? '2px solid #e87722' : '2px solid transparent' }}>
              {t === 'new' ? 'New Request' : 'My Requests'}
            </button>
          ))}
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: 20 }}>
          {tab === 'new' && (
            done ? (
              <div style={{ textAlign: 'center', padding: '30px 0' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
                <div style={{ fontSize: 17, fontWeight: 700, color: '#065f46', marginBottom: 8 }}>Request Submitted</div>
                <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 24 }}>Your project manager and the office have been notified. You'll get a notice when it's answered.</div>
                <button onClick={() => { setDone(false); setForm({ start_date: '', end_date: '', reason: '' }); }}
                  style={{ background: '#e87722', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 28px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                  Submit Another
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 6 }}>Start Date</label>
                    <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                      style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '11px 12px', fontSize: 15, color: '#111' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 6 }}>End Date</label>
                    <input type="date" value={form.end_date} min={form.start_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                      style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '11px 12px', fontSize: 15, color: '#111' }} />
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 6 }}>Reason (optional)</label>
                  <textarea value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} rows={3} placeholder="Vacation, personal, medical, etc."
                    style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '11px 12px', fontSize: 15, resize: 'none', fontFamily: 'inherit', color: '#111' }} />
                </div>
                {err && <div style={{ color: '#dc2626', fontSize: 13 }}>{err}</div>}
                <button onClick={submit} disabled={submitting || !form.start_date || !form.end_date}
                  style={{ width: '100%', padding: '14px 0', background: (!form.start_date || !form.end_date) ? '#e5e7eb' : '#e87722', color: (!form.start_date || !form.end_date) ? '#9ca3af' : '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                  {submitting ? 'Submitting…' : 'Submit Request'}
                </button>
              </div>
            )
          )}

          {tab === 'history' && (
            myRequests.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px 0', fontSize: 14 }}>No requests yet</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {myRequests.map(r => (
                  <div key={r.id} style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>
                        {r.start_date === r.end_date ? r.start_date : `${r.start_date} → ${r.end_date}`}
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 6, padding: '3px 8px', textTransform: 'uppercase', ...statusStyle(r.status) }}>{r.status}</span>
                    </div>
                    {r.reason && <div style={{ fontSize: 13, color: '#6b7280' }}>{r.reason}</div>}
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Submitted {new Date(r.created_at).toLocaleDateString()}</div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// Bulk-fuel logging from the driver's phone. Auto-fills the truck assigned to
// this driver (via the truck's "Assigned To") and its last odometer; they enter
// today's odometer + gallons. Logged for IFTA.
function FuelModal({ worker, onClose }) {
  const [trucks, setTrucks] = useState([]);
  const [truckId, setTruckId] = useState('');
  const [odometer, setOdometer] = useState('');
  const [gallons, setGallons] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    apiFetch('/fuel-trucks').then(list => {
      const arr = Array.isArray(list) ? list : [];
      setTrucks(arr);
      const myName = `${worker?.firstName || ''} ${worker?.lastName || ''}`.trim().toLowerCase();
      const mine = myName && arr.find(t => (t.assigned_to || '').trim().toLowerCase() === myName);
      if (mine) { setTruckId(mine.id); if (mine.meter_reading != null) setOdometer(String(mine.meter_reading)); }
    }).catch(() => {});
  }, []);

  function pickTruck(id) {
    setTruckId(id);
    const t = trucks.find(x => x.id === id);
    setOdometer(t && t.meter_reading != null ? String(t.meter_reading) : '');
  }

  const truck = trucks.find(t => t.id === truckId);
  const prevMiles = truck && truck.meter_reading != null ? Number(truck.meter_reading).toLocaleString('en-US') : null;

  async function submit() {
    if (!truckId) { setErr('Select your truck'); return; }
    if (!gallons) { setErr('Enter gallons'); return; }
    setSaving(true); setErr('');
    try {
      await apiFetch('/fuel-logs', { method: 'POST', body: JSON.stringify({ equipment_id: truckId, filled_at: new Date().toISOString().slice(0, 10), odometer, gallons, note }) });
      setDone(true);
    } catch (e) { setErr('Could not save — try again'); } finally { setSaving(false); }
  }

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 4000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' };
  const sheet = { background: 'var(--card, #fff)', width: '100%', maxWidth: 480, borderRadius: '18px 18px 0 0', padding: 20, maxHeight: '90vh', overflowY: 'auto' };
  const inp = { border: '1px solid var(--border, #e5e7eb)', borderRadius: 10, padding: '12px 12px', fontSize: 16, width: '100%', boxSizing: 'border-box', background: 'var(--card, #fff)', color: 'var(--text)' };
  const lbl = { fontSize: 12, fontWeight: 700, color: 'var(--gray-muted, #6b7280)', textTransform: 'uppercase', marginBottom: 4, display: 'block' };

  return createPortal(
    <div style={overlay} onClick={onClose}>
      <div style={sheet} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text)' }}>⛽ Log Fuel</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: 'var(--gray-muted)' }}>×</button>
        </div>

        {done ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 44, marginBottom: 8 }}>✅</div>
            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--text)', marginBottom: 4 }}>Fuel logged</div>
            <div style={{ fontSize: 13, color: 'var(--gray-muted)', marginBottom: 18 }}>{gallons} gal{truck ? ` · ${truck.name}` : ''}</div>
            <button onClick={onClose} style={{ width: '100%', padding: 14, background: '#b45309', color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>Done</button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={lbl}>Truck</label>
              <select value={truckId} onChange={e => pickTruck(e.target.value)} style={inp}>
                <option value="">— Select truck —</option>
                {trucks.map(t => <option key={t.id} value={t.id}>{t.asset_number ? `${t.asset_number} · ` : ''}{t.name}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Odometer (miles)</label>
              <input type="number" inputMode="numeric" value={odometer} onChange={e => setOdometer(e.target.value)} style={inp} placeholder="Current mileage" />
              {prevMiles && <div style={{ fontSize: 12, color: 'var(--gray-muted)', marginTop: 4 }}>Last recorded: {prevMiles} mi — update to today's reading</div>}
            </div>
            <div>
              <label style={lbl}>Gallons</label>
              <input type="number" inputMode="decimal" step="0.1" value={gallons} onChange={e => setGallons(e.target.value)} style={inp} placeholder="Gallons pumped" />
            </div>
            <div>
              <label style={lbl}>Note (optional)</label>
              <input value={note} onChange={e => setNote(e.target.value)} style={inp} placeholder="Optional" />
            </div>
            {err && <div style={{ color: '#dc2626', fontSize: 14 }}>{err}</div>}
            <button onClick={submit} disabled={saving || !truckId || !gallons}
              style={{ width: '100%', padding: 14, background: (saving || !truckId || !gallons) ? '#d1b48f' : '#b45309', color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>
              {saving ? 'Saving…' : 'Log Fuel'}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// Photo-first upload: pick a job, snap or choose photos, send. Files attach to the
// job's conversation (an optional caption becomes the note).
function PhotoUploadModal({ projects, defaultProjectId, onClose }) {
  const [projectId, setProjectId] = useState(defaultProjectId || (projects.length === 1 ? projects[0].id : ''));
  const [caption, setCaption] = useState('');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  function add(e) { setFiles(p => [...p, ...Array.from(e.target.files || [])]); }
  function remove(i) { setFiles(p => p.filter((_, x) => x !== i)); }
  async function submit() {
    if (!projectId) { setErr('Pick a job'); return; }
    if (files.length === 0) { setErr('Add at least one photo'); return; }
    setBusy(true); setErr('');
    try {
      const token = getToken();
      const fd = new FormData(); files.forEach(f => fd.append('files', f));
      const up = await fetch(`${API_BASE}/upload`, { method: 'POST', headers: token ? { 'x-worker-token': token } : {}, body: fd });
      const upData = await up.json().catch(() => ({}));
      if (!up.ok) throw new Error(upData.error || 'Photo upload failed');
      await apiFetch(`/projects/${projectId}/notes`, { method: 'POST', body: JSON.stringify({ note: caption.trim() || 'Photos from the field', mediaUrls: upData.urls || [] }) });
      setDone(true);
    } catch (e) { setErr('Could not upload — try again'); } finally { setBusy(false); }
  }
  const inp = { width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: 10, border: '1.5px solid #ddd', fontSize: 15, color: '#111', background: '#fff' };
  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: '18px 18px 0 0', padding: '22px 20px 40px' }} onClick={e => e.stopPropagation()}>
        {done ? (
          <>
            <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 8, color: '#111' }}>Photos sent ✓</div>
            <p style={{ color: '#666', fontSize: 14, margin: '0 0 20px' }}>Added to the job.</p>
            <button onClick={onClose} style={{ width: '100%', padding: '14px 0', background: '#e87722', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>Done</button>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#111' }}>Add Photos</div>
              <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 26, cursor: 'pointer', color: '#999', lineHeight: 1 }}>×</button>
            </div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 800, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Job {defaultProjectId && projectId === defaultProjectId ? '· clocked in' : ''}</label>
            <div style={{ marginBottom: 16 }}>
              <JobSearchSelect value={projectId} projects={projects} onChange={setProjectId} />
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
              {/* A straight-to-camera shortcut on phones… */}
              <label style={{ flex: 1, textAlign: 'center', background: '#111', color: '#fff', borderRadius: 12, padding: '14px 0', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
                📷 Take Photo
                <input type="file" accept="image/*" capture="environment" multiple style={{ display: 'none' }} onChange={add} />
              </label>
              {/* …and the everything button: a photo, a PDF, or a drag-and-drop. */}
              <AttachButton multiple variant="plain" label="Choose or Drop" icon="📎"
                style={{ flex: 1, padding: '14px 0', borderRadius: 12, fontWeight: 800, border: '2px solid #111', color: '#111' }}
                onFiles={fs => setFiles(p => [...p, ...fs])} />
            </div>
            {files.length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                {files.map((f, i) => (
                  <AttachThumb key={i} size={72} src={isPdf(f) ? null : URL.createObjectURL(f)} name={f.name} onRemove={() => remove(i)} />
                ))}
              </div>
            )}
            <input value={caption} onChange={e => setCaption(e.target.value)} placeholder="Caption (optional)" style={{ ...inp, marginBottom: 14 }} />
            {err && <div style={{ color: '#d00', fontSize: 13, marginBottom: 12 }}>{err}</div>}
            <button onClick={submit} disabled={busy || !projectId || files.length === 0} style={{ width: '100%', padding: '14px 0', background: (busy || !projectId || files.length === 0) ? '#ccc' : '#e87722', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>{busy ? 'Sending…' : 'Send Photos'}</button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

// Big black/orange home tile for the worker's main actions.
function HomeTile({ icon, label, sublabel, badge, onClick }) {
  return (
    <button onClick={onClick}
      style={{ textAlign: 'left', background: '#fff', border: '1.5px solid #ececec', borderRadius: 16, padding: '16px 14px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 10, position: 'relative' }}>
      <div style={{ width: 46, height: 46, borderRadius: 13, background: '#e87722', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>{icon}</div>
      <div>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#111' }}>{label}</div>
        {sublabel && <div style={{ fontSize: 12, color: '#8a8a8a', marginTop: 1 }}>{sublabel}</div>}
      </div>
      {badge > 0 && <span style={{ position: 'absolute', top: 12, right: 12, background: '#e87722', color: '#fff', fontSize: 12, fontWeight: 800, borderRadius: 20, padding: '2px 8px', lineHeight: 1.4 }}>{badge}</span>}
    </button>
  );
}

export default function Dashboard() {
  const { worker, logout } = useAuth();
  const navigate = useNavigate();
  const [acctMenu, setAcctMenu] = useState(false);
  const [showChangePin, setShowChangePin] = useState(false);
  const isProjectManager = worker?.role === 'ProjectManager';
  const isFieldSupervisor = worker?.role === 'FieldSupervisor';
  const isShopWorker = worker?.role === 'ShopWorker'; // simplified shop-focused app
  const isAccounting = worker?.role === 'Accounting'; // shown as "Admin" — office admin (own tiles)
  const isExecutive = worker?.role === 'Executive';
  const isOfficeLead = isProjectManager || isAccounting || isExecutive; // neither clocks in
  const [showLogTimeOff, setShowLogTimeOff] = useState(false); // Executive / office: log someone's vacation or sick call
  const [showReport, setShowReport] = useState(() => { try { return new URLSearchParams(window.location.search).get('report') === '1'; } catch { return false; } });
  const [showMyCards, setShowMyCards] = useState(() => { try { return new URLSearchParams(window.location.search).get('cards') === '1' ? 'cards' : false; } catch { return false; } });   // ?cards=1 — where the card bells land
  const [showReqTimeOff, setShowReqTimeOff] = useState(false); // PM / office / Executive: their own request (Pat, Sep 23 2026 — the phone home had no way to ask)
  const [showTimeOffMenu, setShowTimeOffMenu] = useState(false); // one Time Off tile asks: mine, or someone's? (Ryan, Oct 1 2026)
  const isFieldLead = isFieldSupervisor || isProjectManager || isExecutive; // Projects/Shop/WorkFlows/Daily template (Executive mirrors PM)
  const [projects, setProjects]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [refreshing, setRefreshing]     = useState(false);
  const [error, setError]               = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showFuelModal, setShowFuelModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [view, setView] = useState('home'); // 'home' (tiles) | 'jobs' (conversation list)
  const [pendingSafety, setPendingSafety] = useState(0);
  const [pretripDue, setPretripDue] = useState(null);
  const [clockedInProjectId, setClockedInProjectId] = useState('');
  const [allProjects, setAllProjects] = useState([]); // all jobs (supervisor daily report — pick any)
  const [projSearch, setProjSearch] = useState(''); // project list filter
  const [projKind, setProjKind] = useState('all');  // 'all' | 'job' | 'wo'
  // Mine is a different question from Job/WO, so it stacks with them rather than
  // replacing them: a PM can ask for their own work orders, which one row of
  // either-or chips could not express.
  const [projMine, setProjMine] = useState(false);
  const [favIds, setFavIds] = useState(() => new Set()); // this user's favorite job ids

  // Pull-to-refresh state
  const touchStartY  = useRef(0);
  const pullDistance = useRef(0);
  const [pullIndicator, setPullIndicator] = useState(0); // 0–100 for progress

  useEffect(() => {
    if (!worker) { navigate('/'); return; }
    load(false);
  }, [worker]);

  // Everyone with app access sees ALL current jobs (organized by favorites), so
  // pull the full current-job list + this user's personal favorites.
  useEffect(() => {
    apiFetch('/projects?scope=current').then(d => setAllProjects(Array.isArray(d) ? d : [])).catch(() => {});
    apiFetch('/project-favorites').then(ids => setFavIds(new Set(Array.isArray(ids) ? ids : []))).catch(() => {});
  }, []);

  async function toggleFavorite(e, id) {
    e.stopPropagation();
    const isFav = favIds.has(id);
    setFavIds(prev => { const n = new Set(prev); isFav ? n.delete(id) : n.add(id); return n; });
    try { await apiFetch(`/project-favorites/${id}`, { method: isFav ? 'DELETE' : 'POST' }); }
    catch { setFavIds(prev => { const n = new Set(prev); isFav ? n.add(id) : n.delete(id); return n; }); }
  }

  async function load(forceRefresh = false) {
    setError(null);
    forceRefresh ? setRefreshing(true) : setLoading(true);
    try {
      const [data, safety, pre, clock] = await Promise.all([apiFetch('/my-projects'), apiFetch('/safety/pending').catch(() => []), apiFetch('/pretrip-status').catch(() => null), apiFetch('/time/status').catch(() => null)]);
      setProjects(Array.isArray(data) ? data : []);
      setPendingSafety(Array.isArray(safety) ? safety.length : 0);
      setPretripDue(pre && pre.needsPretrip ? pre : null);
      setClockedInProjectId(clock && clock.projectId ? clock.projectId : '');
    } catch (e) {
      setError('Could not load projects. Pull down to refresh.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  // The clocked-in job is read once when the dashboard loads, so it goes stale
  // the moment someone switches jobs. Re-read it before opening a modal that
  // prefills from it, so the job on the form is the job they're actually on.
  async function openWithClockedInJob(show) {
    try {
      const clock = await apiFetch('/time/status');
      setClockedInProjectId(clock && clock.projectId ? clock.projectId : '');
    } catch { /* keep whatever we last knew */ }
    show(true);
  }

  // ── Pull-to-refresh handlers ──────────────────────────────
  function onTouchStart(e) {
    touchStartY.current = e.touches[0].clientY;
  }

  function onTouchMove(e) {
    const scrollTop = e.currentTarget.scrollTop;
    if (scrollTop > 0) return; // only trigger at top of list
    const dist = Math.max(0, e.touches[0].clientY - touchStartY.current);
    pullDistance.current = dist;
    setPullIndicator(Math.min(100, dist / 80 * 100));
  }

  function onTouchEnd() {
    if (pullDistance.current > 80 && !refreshing) {
      load(true);
    }
    pullDistance.current = 0;
    setPullIndicator(0);
  }

  function getInitials(name) {
    return (name || '').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  }

  const today = new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  return (
    <div
      className="page"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="top-bar">
        <div className="top-bar-wordmark">TITAN <span>ELECTRIC</span></div>
        <div className="top-bar-right">
          {/* The field is who gets sent things, so this is where the bell was most
              missing. Same component the desktop uses, hung under the bar. */}
          <NotificationBell placement="topbar" />
          {isOfficeLead && (
            <button onClick={() => { setPreferredView('desktop'); navigate(desktopHome(worker?.role)); }}
              title="Switch to desktop view"
              style={{ background:'#e87722', border:'none', color:'#fff', fontSize:12, fontWeight:700, padding:'6px 11px', borderRadius:16, cursor:'pointer', whiteSpace:'nowrap' }}>
              🖥 Desktop
            </button>
          )}
          <div style={{textAlign:'right'}}>
            <div className="top-bar-title">{worker?.firstName || worker?.fullName?.split(' ')[0]}</div>
            <div className="top-bar-sub">{today}</div>
          </div>
          <div style={{ position: 'relative' }}>
            <div className="avatar" onClick={() => setAcctMenu(m => !m)}>
              {worker?.avatarUrl
                ? <img src={`${worker.avatarUrl}/50.png`} alt={worker.firstName} />
                : <span>{getInitials(`${worker?.firstName || ''} ${worker?.lastName || ''}`)}</span>
              }
            </div>
            {acctMenu && (
              <>
                <div onClick={() => setAcctMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 41, background: '#fff', borderRadius: 10, boxShadow: '0 8px 28px rgba(0,0,0,0.18)', border: '1px solid #e5e7eb', minWidth: 160, overflow: 'hidden' }}>
                  <button onClick={() => { setAcctMenu(false); navigate('/my-updates'); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '11px 14px', background: 'none', border: 'none', textAlign: 'left', fontSize: 14, color: '#111827', cursor: 'pointer' }}>
                    📝 My Updates
                  </button>
                  <button onClick={() => { setAcctMenu(false); setShowChangePin(true); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '11px 14px', background: 'none', border: 'none', borderTop: '1px solid #f3f4f6', textAlign: 'left', fontSize: 14, color: '#111827', cursor: 'pointer' }}>
                    🔑 Change PIN
                  </button>
                  <button onClick={() => { setAcctMenu(false); if (window.confirm('Sign out?')) { logout(); navigate('/'); } }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '11px 14px', background: 'none', border: 'none', borderTop: '1px solid #f3f4f6', textAlign: 'left', fontSize: 14, color: '#dc2626', cursor: 'pointer' }}>
                    ↩ Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {showChangePin && <ChangePinModal onClose={() => setShowChangePin(false)} />}
      {showLogTimeOff && <LogTimeOffSheet onClose={() => setShowLogTimeOff(false)} />}
      {showReport && <ReportIssueModal title="🐞 Report a problem or ask for something" onClose={() => setShowReport(false)} />}
      {showMyCards && <MyInfoSheet initial={showMyCards === 'cards' ? 'cards' : null} onClose={() => setShowMyCards(false)} />}
      {showReqTimeOff && <TimeOffRequestSheet onClose={() => setShowReqTimeOff(false)} />}
      {/* The one Time Off tile, for people who can do both: ask for their own, or log someone's. */}
      {showTimeOffMenu && createPortal(
        <div onClick={() => setShowTimeOffMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 5000, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-end' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', width: '100%', borderRadius: '18px 18px 0 0', padding: '18px 16px calc(18px + env(safe-area-inset-bottom))', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#111', marginBottom: 4 }}>Time off</div>
            <button onClick={() => { setShowTimeOffMenu(false); setShowReqTimeOff(true); }}
              style={{ textAlign: 'left', padding: '14px 16px', borderRadius: 14, border: '1.5px solid #ececec', background: '#fff', cursor: 'pointer' }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#111' }}>🌴 Request my time off</div>
              <div style={{ fontSize: 12.5, color: '#8a8a8a', marginTop: 2 }}>Vacation, sick or personal day for me</div>
            </button>
            <button onClick={() => { setShowTimeOffMenu(false); setShowLogTimeOff(true); }}
              style={{ textAlign: 'left', padding: '14px 16px', borderRadius: 14, border: '1.5px solid #ececec', background: '#fff', cursor: 'pointer' }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#111' }}>📅 Log someone's time off</div>
              <div style={{ fontSize: 12.5, color: '#8a8a8a', marginTop: 2 }}>A vacation or a sick call for one of the crew</div>
            </button>
            <button onClick={() => setShowTimeOffMenu(false)} style={{ marginTop: 4, padding: 12, borderRadius: 12, border: 'none', background: '#f3f4f6', color: '#374151', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>, document.body)}

      {/* Pull-to-refresh indicator */}
      {pullIndicator > 0 && (
        <div className="pull-indicator">
          <div
            className="pull-indicator-bar"
            style={{width: `${pullIndicator}%`, opacity: pullIndicator / 100}}
          />
          <span className="pull-indicator-text">
            {pullIndicator >= 100 ? 'Release to refresh' : 'Pull down to refresh'}
          </span>
        </div>
      )}

      {refreshing && (
        <div className="pull-indicator">
          <div className="pull-indicator-bar" style={{width:'100%',opacity:1}}/>
          <span className="pull-indicator-text">
            <div className="spinner" style={{width:12,height:12,borderWidth:2,display:'inline-block',marginRight:6}}/>
            Refreshing…
          </span>
        </div>
      )}

      {/* Executive directive(s) — only render when there's an active one */}
      {view === 'home' && (
        <div style={{ padding: '14px 16px 0' }}>
          {/* The top of the phone home, one look (Pat, Oct 5 2026): blue weekly update, orange
              safety training, red directives — each only when there is one. */}
          <FieldUpdateBanner />
          {pendingSafety > 0 && (
            <div onClick={() => navigate('/safety')} role="button"
              style={{ background: 'linear-gradient(135deg,#f97316,#ea580c)', color: '#fff', borderRadius: 16, padding: '18px 20px', boxShadow: '0 8px 24px rgba(234,88,12,0.32)', marginBottom: 14, cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', opacity: 0.92 }}>
                <span style={{ fontSize: 15, lineHeight: 1 }}>🛡️</span> Safety training required
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1.3, margin: '8px 0 2px' }}>{pendingSafety} item{pendingSafety !== 1 ? 's' : ''} to review and sign</div>
              <div style={{ marginTop: 14 }}>
                <span style={{ display: 'inline-block', background: '#fff', color: '#c2410c', borderRadius: 10, padding: '11px 22px', fontWeight: 800, fontSize: 15 }}>Review now ›</span>
              </div>
            </div>
          )}
          <DirectiveCards />
          {/* questions / RSVPs from the office — nothing renders when none are waiting */}
          <AskCards />
          <WinterizeList />
        </div>
      )}

      {/* Today: your job, time and who's with you — off the published schedule (Ryan, Oct 11 2026). */}
      {view === 'home' && !isOfficeLead && (
        <div style={{ padding: '12px 16px 0' }}><TodaySchedule /></div>
      )}
      {/* ⏰ My Reminders — set from Ask AI ("remind me …") or ＋ Add (Ryan, Oct 26 2026). */}
      {view === 'home' && (
        <div style={{ padding: '12px 16px 0' }}><MyReminders /></div>
      )}

      {/* Home tiles — differ by role */}
      {view === 'home' && (
      <div style={{ padding: '14px 16px 6px', display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 12 }}>
        {isAccounting ? (
          <>
            <HomeTile
              label="Permits" sublabel="Renewals & expirations"
              onClick={() => navigate('/sup/permits')}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M6 3h9l4 4v14H6V3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><path d="M9 12h6M9 15h5M9 9h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>}
            />
            <HomeTile
              label="Timesheets" sublabel="Hours & approvals"
              onClick={() => navigate('/sup/timesheets')}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="13" r="8" stroke="currentColor" strokeWidth="1.8"/><path d="M12 9v4l2.5 1.5M9 3h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
            />
            <HomeTile
              label="Purchase Orders" sublabel="POs to vendors"
              onClick={() => navigate('/sup/purchase-orders')}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M5 3h14v18l-3-2-2 2-2-2-2 2-3-2V3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><path d="M8 8h8M8 11h8M8 14h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>}
            />
            <HomeTile
              label="Contracts" sublabel="Contract status"
              onClick={() => navigate('/sup/workflows/contract')}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M6 3h9l4 4v14H6V3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><path d="M9 13l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
            />
            <HomeTile
              label="Sub Contracts" sublabel="Sub agreements"
              onClick={() => navigate('/sup/workflows/subcontracts')}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M8 11a3 3 0 100-6 3 3 0 000 6ZM16 11a3 3 0 100-6 3 3 0 000 6ZM10 11l4 0M3 20c0-2.5 2-4.5 5-4.5M21 20c0-2.5-2-4.5-5-4.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>}
            />
            <HomeTile
              label="Payroll" sublabel="Certified payroll"
              onClick={() => navigate('/sup/workflows/payroll')}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.8"/><circle cx="12" cy="12" r="2.5" stroke="currentColor" strokeWidth="1.8"/><path d="M6 9v6M18 9v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>}
            />
            <HomeTile
              label="Submittals" sublabel="Approvals across jobs"
              onClick={() => navigate('/sup/workflows/submittals')}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M6 3h9l4 4v14H6V3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><path d="M9 12h6M9 15h6M9 9h3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>}
            />
          </>
        ) : isShopWorker ? (
          <>
            <HomeTile
              label="Shop Orders" sublabel="Deliveries & prefab"
              onClick={() => navigate('/sup/shop?tab=shop_order')}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M3 8l9-5 9 5v8l-9 5-9-5V8Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><path d="M3 8l9 5 9-5M12 13v8" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/></svg>}
            />
            <HomeTile
              label="Equipment" sublabel="Fleet & rentals"
              onClick={() => navigate('/sup/shop?tab=equipment')}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M14 7a3 3 0 10-4 4l-6 6 3 3 6-6a3 3 0 004-4l-2 2-2-2 2-2-1-1Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/></svg>}
            />
            <HomeTile
              label="Material" sublabel="Receive & approved"
              onClick={() => navigate('/sup/shop?tab=material')}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M3 9h18v10H3V9ZM3 9l3-4h12l3 4M8 9v10M16 9v10" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/></svg>}
            />
            <HomeTile
              label="Purchase Orders" sublabel="Write new · search"
              onClick={() => navigate('/shop/pos')}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M8 3h8l4 4v14H4V3h4Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><path d="M9 13h6M12 10v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>}
            />
            <HomeTile
              label="Driver Tools" sublabel="Fuel, inspections, incidents"
              onClick={() => navigate('/sup/shop?tab=driver')}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M3 7h11v8H3V7ZM14 10h4l3 3v2h-7v-5ZM7 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3ZM18 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/></svg>}
            />
            <HomeTile
              label="Time Clock" sublabel="Clock in / out"
              onClick={() => navigate('/time')}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/><path d="M12 7v5l3.5 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
            />
            <HomeTile
              label="My Schedule" sublabel="Your week & who you're with"
              onClick={() => setShowSchedule(true)}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8"/><path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/><path d="M8 14h3M8 17h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>}
            />
            <HomeTile
              label="Safety" sublabel="Training, docs & JHA" badge={pendingSafety}
              onClick={() => setView('safety')}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
            />
            <HomeTile
              label="Daily Report" sublabel="Log a shop / job update"
              onClick={() => openWithClockedInJob(setShowNoteModal)}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M6 3h9l4 4v14H6V3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><path d="M9 12h7M9 15h7M9 9h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>}
            />
          </>
        ) : isFieldLead ? (
          <>
            {!isOfficeLead && (
              <HomeTile
                label="Time Clock" sublabel="Clock in / out"
                onClick={() => navigate('/time')}
                icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/><path d="M12 7v5l3.5 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              />
            )}
            {!isOfficeLead && (
              <HomeTile
                label="My Schedule" sublabel="Your week & who you're with"
                onClick={() => setShowSchedule(true)}
                icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8"/><path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/><path d="M8 14h3M8 17h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>}
              />
            )}
            <HomeTile
              label="Projects" sublabel="Your jobs"
              onClick={() => setView('jobs')}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/></svg>}
            />
            <HomeTile
              label="Shop" sublabel="Safety, orders, equipment…"
              onClick={() => navigate('/sup/shop')}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M4 8h16l-1 12H5L4 8ZM4 8l2-4h12l2 4M9 12v0M15 12v0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
            />
            {/* PM/Exec get every board; a Field Supervisor's board list is trimmed to
                the scheduled work flows (see AdminWorkflows shownTypeKeys). */}
            <HomeTile
              label="Work Flows" sublabel={isFieldSupervisor ? 'Drilling, loops, NVE, USA digs…' : 'Drilling, loops, etc.'}
              onClick={() => navigate('/sup/workflows')}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="7" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.8"/><rect x="14" y="14" width="7" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.8"/><path d="M10 7h4a3 3 0 013 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
            />
            {/* PMs and Executives get the Daily Updates hub here (podcast, written
                updates, activity); Field Supervisors keep the submittals shortcut. */}
            {(isExecutive || isProjectManager) ? (
              <HomeTile
                label="Daily Updates" sublabel={isExecutive ? 'Podcast, updates & activity' : 'Podcast, your updates & work flows'}
                onClick={() => navigate('/daily')}
                icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M5 3h11l3 3v15H5V3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><path d="M8 9h7M8 13h7M8 17h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>}
              />
            ) : (
              <HomeTile
                label="Submittals" sublabel="Approved specs & cut sheets"
                onClick={() => navigate('/sup/submittals')}
                icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M6 3h9l4 4v14H6V3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><path d="M9 13l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              />
            )}
            <HomeTile
              label="Daily Report" sublabel="Defaults to your clocked-in job"
              onClick={() => openWithClockedInJob(setShowNoteModal)}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M6 3h9l4 4v14H6V3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><path d="M9 12h7M9 15h7M9 9h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>}
            />
            {worker?.canCreatePo && (
              <HomeTile
                label="Write PO" sublabel="Defaults to your clocked-in job"
                onClick={() => navigate('/create-po')}
                icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M8 3h8l4 4v14H4V3h4Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><path d="M9 13h6M12 10v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>}
              />
            )}
            {isOfficeLead && (
              <HomeTile
                label="Bids" sublabel="Estimates & proposals"
                onClick={() => navigate('/sup/bids')}
                icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M7 3h10v18l-5-3-5 3V3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><path d="M9 8h6M9 11h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>}
              />
            )}
          </>
        ) : (
          <>
            <HomeTile
              label="Time Clock" sublabel="Clock in / out"
              onClick={() => navigate('/time')}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/><path d="M12 7v5l3.5 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
            />
            <HomeTile
              label="My Schedule" sublabel="Your week & who you're with"
              onClick={() => setShowSchedule(true)}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8"/><path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/><path d="M8 14h3M8 17h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>}
            />
            <HomeTile
              label="Safety" sublabel="Training, docs & JHA" badge={pendingSafety}
              onClick={() => setView('safety')}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
            />
            {/* Every field person fuels trucks, does pre-trips and may have to report an accident (Pat, Oct 9 2026). */}
            <HomeTile
              label="Driver Tools" sublabel="Fuel, inspections, incidents"
              onClick={() => navigate('/sup/shop?tab=driver&home=1')}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M3 7h11v8H3V7ZM14 10h4l3 3v2h-7v-5ZM7 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3ZM18 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/></svg>}
            />
            <HomeTile
              label="Projects" sublabel="All current jobs"
              onClick={() => { setProjSearch(''); setView('jobs'); }}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/></svg>}
            />
            <HomeTile
              label="Add Photos" sublabel="Attach to a job"
              onClick={() => openWithClockedInJob(setShowPhotoModal)}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="3" y="6" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.8"/><circle cx="12" cy="12.5" r="3" stroke="currentColor" strokeWidth="1.8"/><path d="M8 6l1.5-2h5L16 6" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/></svg>}
            />
            {worker?.canCreatePo && (
              <HomeTile
                label="Purchase Orders" sublabel="Write new · search"
                onClick={() => navigate('/shop/pos')}
                icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M8 3h8l4 4v14H4V3h4Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><path d="M9 13h6M12 10v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>}
              />
            )}
          </>
        )}
        {/* My Information — everyone (Ryan, Oct 14 2026; was My Cards, Oct 5): general info, cards, past updates, answers. */}
        <HomeTile
          label="My Information" sublabel="Cards, updates, answers…"
          onClick={() => setShowMyCards(true)}
          icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="2.5" y="5" width="19" height="14" rx="2" stroke="currentColor" strokeWidth="1.8"/><circle cx="8.5" cy="11" r="2.2" stroke="currentColor" strokeWidth="1.6"/><path d="M5 16c.6-1.5 1.9-2.3 3.5-2.3S11.4 14.5 12 16M14 10h5M14 13.5h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>}
        />
        {/* Report a problem — everyone (Pat, Oct 5 2026): lands in App Issues & Requests on the Executive page. */}
        <HomeTile
          label="Report a Problem" sublabel="Something wrong, or an idea"
          onClick={() => setShowReport(true)}
          icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 3l9.5 16.5h-19L12 3z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><path d="M12 10v4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/><circle cx="12" cy="17" r="1" fill="currentColor"/></svg>}
        />
        {/* One Time Off tile (Ryan, Oct 1 2026): a PM asks for their own; an Executive or the
            office can also log someone else's, so they get a choice first. */}
        {isOfficeLead && (
          <HomeTile
            label="Time Off" sublabel={(isExecutive || isAccounting) ? 'Request mine, or log someone\'s' : 'Vacation, sick or personal day'}
            onClick={() => ((isExecutive || isAccounting) ? setShowTimeOffMenu(true) : setShowReqTimeOff(true))}
            icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8"/><path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/><path d="M9 15l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
          />
        )}
        {/* Every desktop page as a tile — the full desktop from the phone (Ryan, Oct 1 2026). */}
        {isOfficeLead && (
          <HomeTile
            label="Desktop Pages" sublabel="Every desktop page, full size"
            onClick={() => navigate('/desktop-pages')}
            icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.8"/><path d="M8 20h8M12 16v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/><path d="M7 8h4M7 11h7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>}
          />
        )}
        {isExecutive && (
          <HomeTile
            label="Directives" sublabel="Send & track priorities"
            onClick={() => navigate('/directives')}
            icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 2l3 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8 5.9 21l1.2-6.8-5-4.9 6.9-1L12 2z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/></svg>}
          />
        )}
        {worker?.isShop && (
          <HomeTile
            label="Receive Material" sublabel="Log deliveries & photos"
            onClick={() => navigate('/sup/shop?tab=material')}
            icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M3 8l9-5 9 5v8l-9 5-9-5V8Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><path d="M3 8l9 5 9-5M12 13v8" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/></svg>}
          />
        )}
      </div>
      )}

      {pretripDue && (
        <div onClick={() => navigate('/pretrip')}
          style={{ margin: '12px 16px 0', background: '#ecfdf5', border: '1.5px solid #a7f3d0', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
          <span style={{ fontSize: 24 }}>🚛</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#065f46' }}>Start today's pre-trip inspection</div>
            <div style={{ fontSize: 12, color: '#047857' }}>{pretripDue.truck?.name || 'Your CDL truck'} — required before you drive</div>
          </div>
          <span style={{ color: '#059669', fontSize: 18 }}>→</span>
        </div>
      )}

      {(view === 'jobs' || view === 'favorites') && (
        <>
          <div style={{ padding: '4px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => setView('home')} style={{ background: 'none', border: 'none', fontSize: 24, color: '#111', cursor: 'pointer', padding: 0, lineHeight: 1 }}>‹</button>
            <div className="section-head" style={{ padding: 0 }}>{view === 'favorites' ? '★ FAVORITES' : 'PROJECTS'}</div>
          </div>

          <div style={{ padding: '4px 16px 8px' }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ position: 'absolute', left: 14, color: 'var(--gray-muted)', pointerEvents: 'none' }}><circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/><path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              <input
                value={projSearch}
                onChange={e => setProjSearch(e.target.value)}
                placeholder={view === 'favorites' ? 'Search favorites…' : 'Search all jobs…'}
                inputMode="search"
                style={{ width: '100%', boxSizing: 'border-box', padding: '11px 38px 11px 40px', fontSize: 16, border: '1px solid #e5e7eb', borderRadius: 999, background: '#f8fafc', outline: 'none' }}
              />
              {projSearch && (
                <button onClick={() => setProjSearch('')} style={{ position: 'absolute', right: 8, width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e5e7eb', border: 'none', borderRadius: 999, color: '#6b7280', fontSize: 15, cursor: 'pointer', padding: 0 }}>×</button>
              )}
            </div>
            {/* Job / WO — the crew usually knows which of the two they're after, and
                the list is long enough that saying so beats scrolling. */}
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              {[['all', 'All'], ['job', 'Job'], ['wo', 'WO']].map(([k, label]) => (
                <button key={k} onClick={() => setProjKind(k)}
                  style={{ flex: 1, padding: '9px 0', borderRadius: 999, fontSize: 13, fontWeight: 800, cursor: 'pointer',
                    border: projKind === k ? '1.5px solid #e87722' : '1.5px solid #e5e7eb',
                    background: projKind === k ? '#fff7ed' : '#fff',
                    color: projKind === k ? '#e87722' : '#6b7280' }}>
                  {label}
                </button>
              ))}
              {/* Only offered to someone who actually has jobs — a button that can
                  only ever return nothing is worse than no button. */}
              {projects.some(p => p.pm_id && p.pm_id === worker?.id) && (
                <button onClick={() => setProjMine(v => !v)}
                  style={{ flex: 1, padding: '9px 0', borderRadius: 999, fontSize: 13, fontWeight: 800, cursor: 'pointer',
                    border: projMine ? '1.5px solid #1e3a5f' : '1.5px solid #e5e7eb',
                    background: projMine ? '#eff6ff' : '#fff',
                    color: projMine ? '#1e3a5f' : '#6b7280' }}>
                  Mine
                </button>
              )}
            </div>
          </div>

          {loading && (
            <div className="center-message"><div className="spinner" /><span>Loading jobs…</span></div>
          )}
          {error && (
            <div className="error-banner" onClick={() => load(true)} style={{ cursor: 'pointer' }}>{error} Tap to retry.</div>
          )}
          {!loading && !error && (() => {
            const q = projSearch.trim().toLowerCase();
            let list = allProjects;
            if (view === 'favorites') list = list.filter(p => favIds.has(p.id));
            if (projKind !== 'all') list = list.filter(p => isWorkOrder(p) === (projKind === 'wo'));
            if (projMine) list = list.filter(p => p.pm_id && p.pm_id === worker?.id);
            list = sortByJobNumber(list);
            if (q) list = list.filter(p => `${jobNumber(p)} ${p.name || ''} ${p.description || ''}`.toLowerCase().includes(q));
            if (list.length === 0) return (
              <div className="center-message">
                <p>{q ? `No jobs match “${projSearch}”.` : view === 'favorites' ? 'No favorites yet.' : projMine ? 'None of these are yours right now.' : projKind === 'wo' ? 'No work orders right now.' : projKind === 'job' ? 'No jobs right now.' : 'No current jobs found.'}</p>
                {view === 'favorites' && !q && <p style={{ fontSize: 13, color: 'var(--gray-muted)' }}>Tap the ☆ star on any job to add it here.</p>}
              </div>
            );
            return list.map(p => {
              const fav = favIds.has(p.id);
              return (
                <div key={p.id} className="job-card" onClick={() => navigate(`/sup/project/${p.id}`)} style={{ cursor: 'pointer' }}>
                  <div className="job-card-top" style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <div className="job-name" style={{ fontSize: 17, fontWeight: 700, flex: 1 }}>
                      {jobNumber(p) && <span style={{ color: '#e87722' }}>{jobNumber(p)} · </span>}{jobName(p)}
                    </div>
                    <button onClick={e => toggleFavorite(e, p.id)} title={fav ? 'Remove from favorites' : 'Add to favorites'}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, lineHeight: 1, padding: 0, color: fav ? '#f59e0b' : '#d1d5db', flexShrink: 0 }}>
                      {fav ? '★' : '☆'}
                    </button>
                  </div>
                  {p.description && (
                    <div style={{ fontSize: 12, color: 'var(--gray-muted)', marginTop: 6, lineHeight: 1.4 }}>
                      {p.description.slice(0, 100)}{p.description.length > 100 ? '…' : ''}
                    </div>
                  )}
                </div>
              );
            });
          })()}
        </>
      )}

      {view === 'safety' && (
        <>
          <div style={{ padding: '4px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => setView('home')} style={{ background: 'none', border: 'none', fontSize: 24, color: '#111', cursor: 'pointer', padding: 0, lineHeight: 1 }}>‹</button>
            <div className="section-head" style={{ padding: 0 }}>SAFETY</div>
          </div>
          <div style={{ padding: '6px 16px', display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 12 }}>
            <HomeTile
              label="Safety Training" sublabel="Assignments" badge={pendingSafety}
              onClick={() => navigate('/safety')}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M4 5.5C6 4 9 4 11 5.5v13C9 17 6 17 4 18.5V5.5ZM11 5.5C13 4 16 4 18 5.5v13C16 17 13 17 11 18.5" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/></svg>}
            />
            <HomeTile
              label="Safety Documents" sublabel="Handbook, policies, SDS"
              onClick={() => navigate('/safety-documents')}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M6 3h8l4 4v14H6V3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
            />
            <HomeTile
              label="Job Hazard Analyses" sublabel="JHAs"
              onClick={() => navigate('/jha')}
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M9 4h6v2H9zM7 5H5v16h14V5h-2" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><path d="M12 3v0M8 11l1.5 1.5L13 9M8 16h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
            />
          </div>
        </>
      )}

      <div style={{height:80}} />
      {showSchedule && <MyScheduleModal onClose={() => setShowSchedule(false)} />}
      {showNoteModal && <DailyNoteModal projects={allProjects.length ? allProjects : projects} defaultProjectId={clockedInProjectId} onClose={() => setShowNoteModal(false)} />}
      {showFuelModal && <FuelModal worker={worker} onClose={() => setShowFuelModal(false)} />}
      {showPhotoModal && <PhotoUploadModal projects={allProjects.length ? allProjects : projects} defaultProjectId={clockedInProjectId} onClose={() => setShowPhotoModal(false)} />}

      {/* One bar, shared with every other phone screen. Its two view buttons hand
          their handlers over; elsewhere the same buttons navigate here. */}
      <MobileBottomNav active={view === 'favorites' ? 'favorites' : 'home'}
        onHome={() => setView('home')}
        onFavorites={() => { setProjSearch(''); setView('favorites'); }} />
    </div>
  );
}
